import { DefaultLabelFormatterCallbackParams, EChartsOption } from 'echarts';
import {
    getPieChartCenterAndRadius,
    getPieGraphLegendSettings,
} from '../RoundCharts/pieChartHelpers';

import { ChartSettingsT } from 'business/definitions';
import { Dataset } from '../../types';
import PolymorphicChartOption from './PolymorphicChartOption';
import { formatDisplay } from 'helpers/formatHelper';
import { getCustomColorMappings, getMappedColor } from '../helpers/getColor';
import { getLegendWidth } from 'helpers/chart/legendHelpers';
import { getTooltipPosition } from 'helpers/eChartHelpers';
import { makeDataset } from 'components/ChartContainer/components/helpers/datasetHelpers';

type ExtremeLabelPositionsT = 'outside' | 'inside';

const INSIDE_LABEL_DEF = {
    color: 'white',
    position: 'inside' as ExtremeLabelPositionsT,
    textBorderColor: 'inherit',
    textBorderWidth: 4,
};

const OUTSIDE_LABEL_DEF = {
    color: 'black',
    position: 'outside' as ExtremeLabelPositionsT,
    labelLine: {
        show: true,
    },
    textBorderWidth: 0,
};
const RAW_DATA_KEY = 'RawXData';

type DSValue = Record<string, string | number>;
type RadiusT = [string, string];
type ProcessedDataT = Record<string, ResponseDataValueT> & {
    [RAW_DATA_KEY]: string;
};

/**
 * Base class for all round charts.
 */
export abstract class RoundChartOption extends PolymorphicChartOption {
    protected isPdfModeActivated: boolean;
    protected isPngModeActivated: boolean;
    protected orderedColumns: {
        x: ResponseColumnT;
        y: ResponseColumnT;
        z?: ResponseColumnT;
    };

    private preprocessedData: ProcessedDataT[];

    constructor(
        orderedColumns: {
            x: ResponseColumnT;
            y: ResponseColumnT;
            z?: ResponseColumnT;
        },
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
        isPdfModeActivated: boolean,
        isPngModeActivated: boolean,
    ) {
        super(orderedColumns, settings, data, width, height);
        this.isPdfModeActivated = isPdfModeActivated;
        this.isPngModeActivated = isPngModeActivated;
        this.orderedColumns = orderedColumns;
        this.preprocessedData = this.getPreprocessedData();
    }

    protected abstract getRadius(): RadiusT;

    /**
     * We need to preprocess the X data to display the correct dimension names (in the legend and the color picker)
     * We keep the raw X data in the dataset to handle properly the click event on ancestors for tree.
     */
    protected getPreprocessedData(): ProcessedDataT[] {
        const { x: xColumn } = this.orderedColumns;
        return this.data
            .map((it) => {
                // This is needed to properly handle unknown cases and the legend when digging.
                const name = formatDisplay(it[xColumn.key], xColumn.type);
                const rawDimension = it[xColumn.key];

                // Undefined/null dimension is not supported for pie chart.
                if (rawDimension === undefined || rawDimension === null) {
                    return undefined;
                }
                return {
                    ...it,
                    [xColumn.key]: name,
                    [RAW_DATA_KEY]:
                        typeof rawDimension === 'string'
                            ? rawDimension
                            : rawDimension.toString(),
                };
            })
            .filter((v) => v !== undefined);
    }

    /**
     * Returns the dimensions used for the round chart, to provide the LegendDimensionContext
     */
    getDimensions(): ResponseDataValueT[] {
        return this.preprocessedData.map((d) => d[this.orderedColumns.x.key]);
    }

    protected getDataset(): Dataset[] {
        return makeDataset(
            this.preprocessedData,
            { ...this.orderedColumns, z: undefined },
            new Set(),
        );
    }

    /**
     * Return the source of the first element in the dataset if exists.
     * This first element acts as a reference to define the legend and its colors.
     */
    protected getDatasetSource(): ResponseDataT[] | undefined {
        if (!this.dataset) return undefined;
        return this.dataset[0]?.source;
    }

    protected getTooltip(): EChartsOption['tooltip'] {
        const { y: yColumn } = this.orderedColumns;
        return {
            trigger: 'item',
            position(
                pos: number[],
                params: FlowAnyObject[],
                el: HTMLDivElement,
                elRect: FlowAnyObject,
                size: FlowAnyObject,
            ) {
                return getTooltipPosition(pos, params, el, elRect, size);
            },
            formatter(param: DefaultLabelFormatterCallbackParams) {
                const { marker, name, value, percent } = param;
                return `${marker} ${name} : ${formatDisplay(
                    (value as DSValue)[yColumn.key],
                    yColumn.type,
                    0, // isExtended
                )} (${percent} %)`;
            },
        };
    }

    /**
     * This function has two main purposes :
     * - define if the labels of each serie should be displayed on the round chart (areLabelsShown)
     *   and how (areLabelsShownOutside). Indeed, this changes text color and text thickness for instance.
     * - define where to put the chart compared to its legend.
     */
    protected getSeries(): EChartsOption['series'] {
        const {
            areLabelsShown,
            areLabelsShownOutside,
            decimalPrecision,
            displayBy,
            showLegend,
            showLegendOnRightSide,
            customPalette,
            palette,
        } = this.settings;
        const { y: yColumn } = this.orderedColumns;
        const customColors = getCustomColorMappings(
            customPalette,
            palette,
            this.orderedColumns.x.key,
        );

        const isExportModeActivated =
            this.isPdfModeActivated || this.isPngModeActivated;
        const baseLabel = {
            show: areLabelsShown,
            transitionDuration: 0,
            formatter(params: DefaultLabelFormatterCallbackParams) {
                const { value, percent } = params;
                // NOTE: hack - we need two line breaks on PDFs otherwise the number and percent overlap
                const lineBreak = isExportModeActivated ? '\n\n' : '\n';
                return `${formatDisplay(
                    (value as DSValue)[yColumn.key],
                    yColumn.type,
                    decimalPrecision,
                    displayBy,
                )}${lineBreak}${formatDisplay(percent ? percent / 100 : 0, 'fraction')}`;
            },
        };
        return [
            {
                type: 'pie',
                ...getPieChartCenterAndRadius(
                    showLegend,
                    showLegendOnRightSide,
                    this.getRadius(),
                    this.height,
                    this.width,
                    areLabelsShownOutside,
                ),
                right: showLegendOnRightSide
                    ? 50 + getLegendWidth(this.width)
                    : 20,
                left: 20,
                top: showLegend && !showLegendOnRightSide ? 40 : 20,
                bottom: 40,
                itemStyle: {
                    borderRadius: 4,
                    borderColor: 'white',
                    color: (params) => {
                        const dataPoint = params.data as ProcessedDataT;
                        const dimension = dataPoint[RAW_DATA_KEY];
                        const index = params.dataIndex;
                        return (
                            getMappedColor(
                                dimension,
                                index,
                                customColors,
                                this.getColor(),
                                false,
                            ) ?? '#000000'
                        );
                    },
                },
                datasetId: 'base',
                encode: {
                    itemName: this.orderedColumns.x.key,
                    value: this.orderedColumns.y.key,
                },
                label: areLabelsShownOutside
                    ? { ...OUTSIDE_LABEL_DEF, ...baseLabel }
                    : { ...INSIDE_LABEL_DEF, ...baseLabel },
                labelLine: {
                    show: areLabelsShown && areLabelsShownOutside,
                },
            },
        ];
    }

    protected getLegend(): EChartsOption['legend'] {
        const { legendPosition, showLegend, showLegendOnRightSide } =
            this.settings;
        const { x: xColumn, y: yColumn } = this.orderedColumns;

        const isExportModeActivated =
            this.isPdfModeActivated || this.isPngModeActivated;

        const datasetSource = this.getDatasetSource();

        const legendData = datasetSource
            ? datasetSource
                  .filter((item: ResponseDataT) => Boolean(item[yColumn.key]))
                  .map((d: ResponseDataT) => ({
                      name: d[xColumn.key] as string,
                  }))
            : [];
        return getPieGraphLegendSettings({
            legendData,
            legendPosition,
            showLegend,
            showLegendOnRightSide,
            isExportModeActivated,
            width: this.width,
        });
    }

    getChartOption(): EChartsOption {
        const option = super.getChartOption();
        return {
            ...option,
            toolbox: {
                show: false,
            },
            legend: this.getLegend(),
            tooltip: this.getTooltip(),
            series: this.getSeries(),
            dataset: this.dataset,
        };
    }
}

export class PieChartOption extends RoundChartOption {
    protected getRadius(): RadiusT {
        return ['0%', '75%'];
    }
}

export class DonutChartOption extends RoundChartOption {
    protected getRadius(): RadiusT {
        return ['50%', '75%'];
    }
}
